/**
 * Mobile Map - 모바일용 Google Maps 지도 컴포넌트
 * 실제 Google Maps API 사용
 */

import { useEffect, useRef, useState } from 'react';
import { HiLocationMarker } from 'react-icons/hi';

// 자기장 강도별 마커 색상
const MARKER_COLORS = {
  strong: '#FF0000',    // 빨강 - 강한 자기장
  medium: '#FFA500',    // 주황 - 중간 자기장
  weak: '#0066FF',      // 파랑 - 약한 자기장
};

// 전역 스크립트 로드 상태 관리
let isGoogleMapsLoading = false;
let googleMapsLoadPromise = null;

/**
 * Google Maps API 스크립트 로드 (싱글톤 패턴)
 */
const loadGoogleMapsScript = () => {
  // 이미 로드되어 있으면 Promise 반환
  if (window.google && window.google.maps) {
    return Promise.resolve();
  }

  // 이미 로딩 중이면 기존 Promise 반환
  if (isGoogleMapsLoading && googleMapsLoadPromise) {
    return googleMapsLoadPromise;
  }

  // 이미 스크립트 태그가 있으면 기다림
  const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
  if (existingScript) {
    googleMapsLoadPromise = new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        if (window.google && window.google.maps) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 100);
    });
    return googleMapsLoadPromise;
  }

  // 새로 로드 시작
  isGoogleMapsLoading = true;
  googleMapsLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    // 환경 변수 확인 또는 기본값 사용
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || 
                   import.meta.env.VITE_GOOGLE_MAP_API_KEY || 
                   'AIzaSyCnSBkbEfyVp8QEuAN27M3G-xJyy6IOGiE';
    
    // API 키 유효성 검사
    if (!apiKey || apiKey === 'your_google_maps_api_key_here') {
      console.error('[MobileMap] Google Maps API key is not set. Please create .env file with VITE_GOOGLE_MAPS_API_KEY');
      isGoogleMapsLoading = false;
      reject(new Error('Google Maps API key is not configured'));
      return;
    }
    
    console.log('[MobileMap] Loading Google Maps API with key:', apiKey.substring(0, 10) + '...');
    
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=marker&loading=async`;
    script.async = true;
    script.defer = true;
    script.id = 'google-maps-script';

    script.onload = () => {
      isGoogleMapsLoading = false;
      resolve();
    };

    script.onerror = () => {
      isGoogleMapsLoading = false;
      console.error('Failed to load Google Maps script');
      reject(new Error('Failed to load Google Maps script'));
    };

    document.head.appendChild(script);
  });

  return googleMapsLoadPromise;
};

export default function MobileMap({ spots = [], onSpotClick }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const currentLocationMarkerRef = useRef(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);

  /**
   * Google Maps SDK 초기화
   */
  useEffect(() => {
    let isMounted = true;

    loadGoogleMapsScript()
      .then(() => {
        // Google Maps API가 완전히 로드되었는지 확인
        const checkGoogleMapsReady = () => {
          return window.google && 
                 window.google.maps && 
                 window.google.maps.Map &&
                 window.google.maps.MapTypeId;
        };

        // API가 준비될 때까지 대기
        const waitForGoogleMaps = () => {
          if (checkGoogleMapsReady()) {
            if (isMounted) {
              initializeMap();
            }
          } else {
            setTimeout(() => {
              if (isMounted) {
                waitForGoogleMaps();
              }
            }, 100);
          }
        };

        waitForGoogleMaps();
      })
      .catch((error) => {
        console.error('Google Maps initialization error:', error);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  /**
   * 지도 인스턴스 초기화
   */
  const initializeMap = () => {
    if (!mapRef.current || mapInstanceRef.current) return;

    if (!window.google || !window.google.maps || !window.google.maps.Map) {
      console.error('Google Maps API is not fully loaded');
      return;
    }

    const { google } = window;

    // 제주도 중심 좌표
    const jejuCenter = { lat: 33.361, lng: 126.529 };

    // 환경 변수에서 Map ID 가져오기
    const mapId = import.meta.env.VITE_GOOGLE_MAPS_MAP_ID || undefined;
    
    const mapOptions = {
      center: jejuCenter,
      zoom: 11, // 모바일에서는 조금 더 확대
      mapTypeId: google.maps.MapTypeId?.ROADMAP || 'roadmap',
      ...(mapId && { mapId: mapId }),
      disableDefaultUI: false, // 줌 컨트롤 등 기본 UI 유지
      zoomControl: true,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      styles: [
        {
          featureType: 'poi',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }],
        },
      ],
    };

    // 지도 생성
    const map = new google.maps.Map(mapRef.current, mapOptions);
    mapInstanceRef.current = map;

    // 현재 위치 마커 추가 (제주도 중심)
    const currentLocationIcon = {
      path: google.maps.SymbolPath.CIRCLE,
      scale: 8,
      fillColor: '#3b82f6',
      fillOpacity: 1,
      strokeColor: '#ffffff',
      strokeWeight: 2,
    };

    currentLocationMarkerRef.current = new google.maps.Marker({
      position: jejuCenter,
      map: map,
      icon: currentLocationIcon,
      title: '현재 위치',
      zIndex: 1000,
    });

    setIsMapLoaded(true);
  };

  /**
   * 스팟 마커 렌더링
   */
  useEffect(() => {
    if (!isMapLoaded || !mapInstanceRef.current) return;

    const { google } = window;
    const map = mapInstanceRef.current;

    // 기존 마커 제거
    markersRef.current.forEach((marker) => {
      if (marker.marker) {
        marker.marker.setMap(null);
      }
    });
    markersRef.current = [];

    // 스팟 마커 생성
    spots.forEach((spot) => {
      const markerColor = MARKER_COLORS[spot.magnetic_field_strength] || MARKER_COLORS.weak;
      
      // SVG 아이콘 생성
      const svgIcon = {
        path: google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,
        scale: 5,
        fillColor: markerColor,
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 2,
        rotation: 0,
        anchor: new google.maps.Point(0, 0),
      };

      // 마커 생성
      const marker = new google.maps.Marker({
        position: {
          lat: typeof spot.latitude === 'number' ? spot.latitude : parseFloat(spot.latitude),
          lng: typeof spot.longitude === 'number' ? spot.longitude : parseFloat(spot.longitude),
        },
        map: map,
        icon: svgIcon,
        title: spot.spot_name,
        animation: google.maps.Animation.DROP,
      });

      // 마커 클릭 이벤트
      marker.addListener('click', () => {
        if (onSpotClick) {
          onSpotClick(spot);
        }
      });

      markersRef.current.push({
        marker,
        spot,
      });
    });
  }, [spots, isMapLoaded, onSpotClick]);

  return (
    <div className="relative w-full h-full">
      {/* Google Maps 지도 컨테이너 */}
      <div
        ref={mapRef}
        className="w-full h-full"
        style={{ minHeight: '100%' }}
      />

      {/* 범례 오버레이 */}
      {isMapLoaded && (
        <div 
          className="absolute bottom-4 left-4 right-4 px-4 py-3 rounded-xl shadow-lg backdrop-blur-md border-2 z-10" 
          style={{ 
            background: 'rgba(255, 255, 255, 0.95)', 
            borderColor: 'var(--color-primary)' 
          }}
        >
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--color-text)' }}>
              <HiLocationMarker className="w-4 h-4 text-blue-500" />
              <span>현재 위치</span>
            </div>
            <div className="flex items-center gap-3 text-xs">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: MARKER_COLORS.strong }} />
                <span style={{ color: 'var(--color-text-secondary)' }}>강함</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: MARKER_COLORS.medium }} />
                <span style={{ color: 'var(--color-text-secondary)' }}>보통</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: MARKER_COLORS.weak }} />
                <span style={{ color: 'var(--color-text-secondary)' }}>약함</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 로딩 표시 */}
      {!isMapLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-0">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: 'var(--color-primary)' }}></div>
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>지도 로딩 중...</p>
          </div>
        </div>
      )}
    </div>
  );
}
